import {assertAndContinue} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Selector} from 'notebook/ELNConstants';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {GroupActionFunctionParameters} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import {performClientSideWriteAction} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  LastSyncedUpstreamValue,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateTableWithColumnUpdatesAndCells from 'notebook/structured-tables/util/updateTableWithColumnUpdatesAndCells';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {handleRefreshStructuredTable} from 'notebook/tables/refresh-table-data/RefreshTableData';
import {RESYNC_TABLE_LABEL, TableData} from 'notebook/tables/TableConstants';
import {hasNLO, isStructuredTable} from 'notebook/TypePredicates';
import {AnyStructuredTable, NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';

/**
 * Refreshes and then returns a contained note part within a noteLinkedGroup.
 *
 * Performance note: Generic is constrained to `AnyStructuredTable` instead of the broader
 * `NotePartItem` type to avoid TypeScript computing all 1,287 members of that union, which
 * would take ~11 seconds to type-check. The function only handles structured tables anyway
 * (validates at runtime and returns early for other types).
 */
export function refreshContainedNotePart<T extends AnyStructuredTable>({
  containedNotePart,
  evaluatedTableMap,
  getIdFromCidFn,
  entryOrTemplateId,
  getItemWithUpdatedContent,
}: {
  containedNotePart: NotePartWithItem<T>;
  evaluatedTableMap: OutputTableMap;
  getIdFromCidFn: (id: string) => string | number | undefined;
  entryOrTemplateId: string;
  getItemWithUpdatedContent: (oldContainedNotePart: NotePart, content: object) => NotePart;
}): Promise<NotePartWithItem<T>> {
  // The `handleRefreshStructuredTable` just calls our callback and doesn't return any value.
  // This is usually fine as we would just call the updateGroup callback inside of our callback
  // function.  But in this case, we want to refresh each note part serially, calling our
  // GroupAdapter.reduceGroup after each one to make sure that our group is consistent before
  // refreshing the next item in the group.  So we wrap the entire operation in a promise which
  // is then resolved as part of the callback function, giving us access to the actual content as
  // the return of this function.
  // oxlint-disable-next-line no-async-promise-executor -- Disabling for now, but worth fixing
  return new Promise(async (resolve) => {
    if (!hasNLO(containedNotePart) || !isStructuredTable(containedNotePart.item)) {
      // We don't currently support refreshing non-structured tables so we just return the
      // unmodified group here.
      const itemType =
        hasNLO(containedNotePart) && isStructuredTable(containedNotePart.item)
          ? containedNotePart.item.type
          : containedNotePart.type;
      assertAndContinue(false, 'Trying to refresh an unexpected contained note part', {itemType});
      resolve(containedNotePart);
      return;
    }
    const structuredTable = containedNotePart.item;
    const structuredTableAdapter: UnknownStructuredTableAdapter =
      getStructuredTableAdapters()[structuredTable.kind];
    const table = structuredTableAdapter.toTable(structuredTable);
    const computedTable = evaluatedTableMap[table.name];
    const onUpdateTableCallback = (
      index: number,
      updatedContent: {
        table: TableData;
        lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
      },
      selectors?: Array<Selector>
    ): void => {
      const updatedContainedNotePart = getItemWithUpdatedContent(
        containedNotePart,
        updatedContent
      ) as NotePartWithItem<T>;
      resolve(updatedContainedNotePart);
    };
    await handleRefreshStructuredTable({
      structuredTable,
      structuredTableAdapter,
      entryOrTemplateId,
      computedTable,
      onUpdateTable: updateTableWithColumnUpdatesAndCells(
        // This index is ignored as we thread things back based upon the cid and the callback above
        0,
        table,
        getIdFromCidFn,
        onUpdateTableCallback,
        (_) => new Set()
      ),
    });
  });
}

/**
 * Refreshes each containedNotePart in the provided NoteLinkedGroup, calling the
 * `NoteLinkedGroupAdapter.reduceGroup` after each refresh before then refreshing the next contained note
 * part.
 *
 * This function assumes that the `reduceGroup` call does not change the length or order of the contained
 * note parts.
 *
 * Each note part is refreshed in order, so if the `reduceGroup` call ends up changing a previous
 * `containedNotePart`, that previous `containedNotePart` will not be refreshed again.
 *
 * @param params
 */
async function refreshNoteLinkedGroup(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): Promise<void> {
  const {
    entryOrTemplateId,
    noteLinkedGroupAdapter,
    getItemWithUpdatedContent,
    noteLinkedGroup,
    updateGroup,
    evaluatedTableMap,
    getIdFromCidFn,
  } = params;
  const refreshTables = async (): Promise<void> => {
    let refreshedGroup = noteLinkedGroup;
    for (let i = 0; i < noteLinkedGroup.containedNoteParts.length; i++) {
      const containedNotePart = noteLinkedGroup.containedNoteParts[i];
      if (hasNLO(containedNotePart)) {
        const updatedContainedNotePart = await refreshContainedNotePart({
          // Type assertion to constrain the generic and avoid TypeScript computing 1,287 union members.
          // The function validates the type at runtime and returns early for non-structured tables.
          containedNotePart: containedNotePart as NotePartWithItem<AnyStructuredTable>,
          evaluatedTableMap,
          getIdFromCidFn,
          entryOrTemplateId,
          getItemWithUpdatedContent,
        });
        refreshedGroup = await noteLinkedGroupAdapter.reduceGroup(noteLinkedGroup, updatedContainedNotePart);
      }
    }
    updateGroup(refreshedGroup);
  };

  await performClientSideWriteAction({
    entryOrTemplateId,
    loadingScreenMessage: 'Refreshing...',
    autoSaverAction: ACTION_REFRESH,
    doPerformAction: refreshTables,
    analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_NOTE_LINKED_GROUP,
    analyticsMetadata: {
      noteLinkedGroupKind: noteLinkedGroup.kind,
    },
  });
}

const refreshNoteLinkedGroupActionFunction = makeSimpleGroupActionFunction<unknown, unknown>({
  label: RESYNC_TABLE_LABEL,
  icon: 'arrows-rotate',
  iconAltText: 'Refresh',
  onInvoke: refreshNoteLinkedGroup,
  getDisabled: ({locked}) => locked,
});

export default refreshNoteLinkedGroupActionFunction;
